import React, { useEffect, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import { useModalManager } from '@/components/common/Modal';
import SlideConditionsIcon from '@/components/common/SlideConditionsIcon';
import useSlideShowStore, { ISlide } from '@/components/admin/SlideShow/stores/useSlideShowStore';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { RemoveSlideModal } from '@/components/admin/SlideShow/Utils/RemoveSlide';
import Slide from '../../Slide';
import builderStyles from '../SlideShowBuilder.module.scss';
import styles from './SlidePanel.module.scss';
import SlideThumbnailActions from './SlideThumbnailActions';
import { makeDraggableId } from './slideReorder';

interface SlideThumbnailProps {
  index: number;
  isSelected: boolean;
  slide: ISlide;
  onSelect: () => void;
}

const SlideThumbnail = ({ index, isSelected, slide, onSelect }: SlideThumbnailProps) => {
  const locale = useSlideShowContext(state => state.locale);
  const setIsSlideConditionsDrawerOpen = useSlideShowStore(state => state.setIsSlideConditionsDrawerOpen);
  const removeModalManager = useModalManager();
  const thumbnailRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isSelected) thumbnailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [isSelected]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete') removeModalManager.open();
  };

  return (
    <Draggable draggableId={makeDraggableId('slide', slide.id)} index={index}>
      {draggable => (
        <div
          ref={(el) => { draggable.innerRef(el); thumbnailRef.current = el; }}
          {...draggable.draggableProps}
          {...draggable.dragHandleProps}
          aria-label={`Slide Thumbnail ${slide.position}`}
          className={builderStyles.dragContainer}
          id={`slide_reel-${slide.id}`}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onMouseDown={onSelect}
        >
          <div className={cn(styles.thumbnailWrapper, `locale-${locale}`, { [styles.selected]: isSelected })}>
            <SlideThumbnailActions removeModalManager={removeModalManager} slide={slide} />

            <span className={styles.positionNumber}>
              {slide.position}
              {slide.condition && (
                <SlideConditionsIcon
                  tooltipContent={I18n.t('slide_conditions_for_slide')}
                  tooltipPlacement="bottom-end"
                  onClick={() => setIsSlideConditionsDrawerOpen(true)}
                />
              )}
            </span>

            <Slide
              className={cn(styles.overflowHidden, styles.noPointerEvents)}
              forDisplay="thumbnail"
              imageModel={slide.imageModel}
              isSelected={isSelected}
              slide={slide}
              slideId={String(slide.id)}
              slideObjects={slide.slideObjects}
              templateObjects={slide.slideTemplate?.slideTemplateObjects}
            />
          </div>

          <RemoveSlideModal modalManager={removeModalManager} slide={slide} />
        </div>
      )}
    </Draggable>
  );
};

export default SlideThumbnail;
